import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { OnboardingProgress } from "@/components/host/onboarding/OnboardingProgress";
import { OnboardingSteps } from "@/components/host/onboarding/OnboardingSteps";

interface HostOnboardingData {
  profile: boolean;
  payment: boolean;
  properties: boolean;
  verification: boolean;
  completed: boolean;
}

export default function HostOnboardingPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState<HostOnboardingData>({
    profile: false,
    payment: false,
    properties: false,
    verification: false,
    completed: false
  });

  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/login");
          return;
        }

        const { data: profile, error } = await supabase
          .from("profiles")
          .select("host_onboarding_data, host_onboarding_step")
          .eq("id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (profile?.host_onboarding_data) {
          const parsedData = typeof profile.host_onboarding_data === 'string'
            ? JSON.parse(profile.host_onboarding_data)
            : profile.host_onboarding_data;
            
          setOnboardingData(parsedData as HostOnboardingData);
          setCurrentStep(profile.host_onboarding_step || 1);
        }
      } catch (error) {
        console.error("Error fetching onboarding status:", error);
        toast({
          title: "Error",
          description: "Failed to load onboarding status",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOnboardingStatus();
  }, [navigate, toast]);

  const updateOnboardingStatus = async (step: keyof HostOnboardingData, completed: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updatedData = {
        ...onboardingData,
        [step]: completed,
      };

      const { error } = await supabase
        .from("profiles")
        .update({
          host_onboarding_data: updatedData,
          host_onboarding_step: currentStep + 1,
          host_onboarding_completed: Object.values(updatedData).every(Boolean),
          role: completed && step === "verification" ? "host" : undefined,
        })
        .eq("id", user.id);

      if (error) throw error;

      setOnboardingData(updatedData);
      setCurrentStep(prev => prev + 1);

      toast({
        title: "Success",
        description: "Onboarding progress updated",
      });
    } catch (error) {
      console.error("Error updating onboarding status:", error);
      toast({
        title: "Error",
        description: "Failed to update onboarding progress",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="p-6">
        <h1 className="text-2xl font-bold mb-6">Host Onboarding</h1>
        
        <OnboardingProgress currentStep={currentStep} totalSteps={4} />
        
        <OnboardingSteps
          currentStep={currentStep}
          onboardingData={onboardingData}
          onComplete={updateOnboardingStatus}
        />

        {Object.values(onboardingData).every(Boolean) && (
          <div className="mt-8 text-center">
            <h3 className="text-xl font-semibold text-green-600 mb-4">
              🎉 Congratulations! You've completed the host onboarding process.
            </h3>
            <Button onClick={() => navigate("/host/dashboard")}>
              Go to Host Dashboard
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
